import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { range } from 'lodash';
import { Textfit } from 'react-textfit';
import Modal from '~ComponentsImproved/Modal';
import Heading from '~ComponentsImproved/Typography/Heading';
import { handyBreakpoint } from '~Styles/breakpoints';
import Button from '~ComponentsImproved/Button';

const Tabs = styled.div`
  display: flex;
  flex-flow: row nowrap;
  background-color: var(--colorBlueGray1200);
  margin-bottom: 16px;
  border-bottom: 1px solid var(--colorBlueGray1000);
`;

const Tab = styled.div<{ active: boolean; small?: boolean }>`
  flex: 1;
  display: flex;
  text-align: center;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 16px;
  border-bottom: 4px solid;
  border-color: ${({ active }) => (active ? 'var(--colorUIYellow)' : 'transparent')};
  background-color: ${({ active }) => (active ? 'black' : 'transparent')};
  color: ${({ active }) => (active ? 'white' : 'var(--colorBlueGray400)')};
  height: ${({ small }) => (small ? '48px' : '68px')};

  ${handyBreakpoint} {
    height: 48px;
  }
`;

const Wrapper = styled.div`
  padding: 0 8px 16px;
`;

const ContentWrapper = styled.div<{
  hasTabs: boolean;
  scaleDownOnHandHeld?: boolean;
  small?: boolean;
}>`
  width: ${({ small }) => (small ? '350px' : '368px')};
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  text-align: center;
  background-color: #000;
  padding-top: ${({ hasTabs }) => (hasTabs ? '0' : '16px')};

  ${({ scaleDownOnHandHeld }) =>
    scaleDownOnHandHeld &&
    `
      ${handyBreakpoint} {
    width: 95%;
    max-width: 350px;
    transform: scale(0.8);
  }
    `}
`;

const Display = styled.div`
  width: 100%;
  font-size: 64px;
  line-height: 64px;
  padding: 0 8px 23px;
  box-sizing: border-box;
  text-align: right;
  color: #ffffff;
  border-bottom: 1px solid rgb(28, 35, 44);
  max-width: 100%;
  overflow: hidden;
  ${handyBreakpoint} {
    padding-bottom: 16px;
  }
`;

const Numpad = styled.div`
  display: grid;
  font-size: 32px;
  grid-template-columns: repeat(auto-fill, minmax(104px, 1fr));
  margin: 16px -4px;
  ${handyBreakpoint} {
    margin: 6px -4px;
  }
`;

const Digit = styled.div`
  background: var(--colorBlueGray1200);
  box-sizing: border-box;
  text-align: center;
  color: #eee;
  margin: 4px;
  border-radius: 4px;
  box-shadow: 0px 2px 8px 0px rgba(0, 0, 0, 0.64);
  padding: 18px 8px 22px;
  font-size: 32px;
  font-weight: normal;
  height: 80px;
  letter-spacing: 0px;
  line-height: 40px;
  ${handyBreakpoint} {
    height: 72px;
  }
  &:active {
    background-image: linear-gradient(
      180deg,
      var(--colorBlueGray1000) 1%,
      var(--colorBlueGray800) 100%
    );
  }
`;

type InputType = 'freeform' | 'percentage';
export type NumpadTab = { title: string; type: InputType; key?: string };
export type SelectedNumpadTab = NumpadTab | null;

type NumberInputProps = {
  initialValue?: string;
  onSubmit: (typedDigits: string, tab: NumpadTab | null) => void;
  onClose?: () => void;
  maxValue?: number;
  maxCount?: number;
  maxDecimals?: number;
  decimal?: boolean;
  title?: string;
  notModal?: boolean;
  small?: boolean;
  submitText?: string;
  tabs?: NumpadTab[];
  initialValueIsPlaceholder?: boolean;
  scaleDownOnHandHeld?: boolean;
};

/**
 * A numpad. onSubmit will return the input value and the active tab if any, undefined otherwise.
 */
const NumberInput = ({
  initialValue = '0',
  onClose,
  onSubmit,
  maxValue,
  maxCount = 7,
  maxDecimals = 2,
  decimal = false,
  title,
  submitText,
  notModal,
  tabs,
  initialValueIsPlaceholder,
  scaleDownOnHandHeld = true,
  small,
}: NumberInputProps) => {
  const [selectedTab, setSelectedTab] = useState<SelectedNumpadTab>(tabs ? tabs[0] : null);
  const [digits, setDigits] = useState(initialValueIsPlaceholder ? '' : initialValue);
  const [maxVal, setMaxVal] = useState(maxValue ?? 9999999);
  const isStorybook = !!process.env.STORYBOOK_MODE;

  function addDigit(n: string | number) {
    // If the whole number or decimal number is too long
    const [wholeDigits, decimalDigits] = digits.split('.');
    const isDecimalInput = digits.includes('.');
    if (
      n !== '.' &&
      ((!isDecimalInput && wholeDigits.length >= maxCount) ||
        (isDecimalInput && decimalDigits.length >= maxDecimals))
    ) {
      return;
    }

    // If the number is too large
    let newValue = digits + n;
    if (!!maxVal && parseFloat(newValue) > maxVal) {
      return;
    }

    //If first input is "0" and more than 2 characters and not decimals
    if (!decimal && newValue.length > 1 && newValue.charAt(0) == '0') {
      newValue = newValue.substring(1);
    }

    // If there are multiple decimals
    if (decimal && newValue.split('.').length > 2) {
      return;
    }
    setDigits(newValue);
  }

  useEffect(() => {
    if (!selectedTab) {
      return;
    }
    switch (selectedTab.type) {
      case 'percentage':
        setMaxVal(100);
        break;
      case 'freeform':
      default:
        setMaxVal(maxValue ?? 9999999);
    }
  }, [selectedTab]);

  const onChangeValue = (value: string | number) => {
    if (digits === '0' && value !== '.') {
      setDigits(value.toString());
    } else {
      addDigit(value);
    }
  };

  const handleSubmit = (typedDigits: string) => {
    setDigits(initialValue);
    onSubmit(typedDigits, selectedTab);
  };

  const NumpadContent = () => (
    <ContentWrapper
      data-test="numpad"
      hasTabs={Boolean(tabs)}
      scaleDownOnHandHeld={scaleDownOnHandHeld}
      small={small}>
      {tabs && (
        <Tabs>
          {tabs.map((tab) => (
            <Tab
              key={tab.title}
              active={selectedTab?.title === tab.title}
              onTouchStart={() => {
                setSelectedTab(tab);
                setDigits(initialValue);
              }}
              onClick={(e) => {
                if (!isStorybook) {
                  return;
                }
                e.stopPropagation();
                setSelectedTab(tab);
                setDigits(initialValue);
              }}
              small={small}>
              {tab.title}
            </Tab>
          ))}
        </Tabs>
      )}
      {title && (
        <Heading level={4} color="white">
          {title}
        </Heading>
      )}
      <Display>
        {initialValueIsPlaceholder && digits === '' ? (
          <Textfit mode="single" max={64}>
            <span
              style={{
                opacity: 0.4,
              }}>
              {initialValue}
            </span>
          </Textfit>
        ) : (
          <Textfit mode="single" max={64}>
            {digits}
          </Textfit>
        )}
      </Display>
      <Wrapper>
        <Numpad>
          {range(1, 10).map((n) => (
            <Digit
              key={n}
              onTouchStart={() => onChangeValue(n)}
              onClick={(e) => {
                if (!isStorybook) {
                  return;
                }
                e.stopPropagation();
                onChangeValue(n);
              }}>
              {n}
            </Digit>
          ))}

          <Digit
            onTouchStart={() => setDigits('0')}
            onClick={(e) => {
              if (!isStorybook) {
                return;
              }
              e.stopPropagation();
              setDigits('0');
            }}>
            C
          </Digit>
          <Digit
            onTouchStart={() => onChangeValue('0')}
            onClick={(e) => {
              if (!isStorybook) {
                return;
              }
              e.stopPropagation();
              onChangeValue('0');
            }}>
            0
          </Digit>

          {decimal ? <Digit onTouchStart={() => onChangeValue('.')}>.</Digit> : <Digit />}
        </Numpad>
        <Button
          text={submitText || 'Ok'}
          onTouchStart={() => handleSubmit(digits)}
          height="72px"
          styleType="Confirm"
        />
      </Wrapper>
    </ContentWrapper>
  );

  return notModal ? (
    <NumpadContent />
  ) : (
    <Modal isShowing={true} hide={onClose} backdropClose={true} noPadding={true}>
      <NumpadContent />
    </Modal>
  );
};

export default NumberInput;
